import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const env = import.meta.env.VITE_SENTRY_ENV || "development";

  if (!dsn) {
    if (import.meta.env.DEV) {
      console.warn("Sentry DSN not found. Sentry is disabled.");
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: env,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],

    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions in dev/staging. Adjust for production.
    
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      "localhost",
      "https://dev-pli.primalarvaebali.com/",
      "https://webapp.primalarvaeindonesia.com/",
    ],

    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
};

/**
 * Capture an error and send it to Sentry manually.
 * Useful for caught errors that still need reporting.
 */
export const captureError = (error: unknown, context?: Record<string, unknown>) => {
  Sentry.captureException(error, { extra: context });
};

/**
 * Capture a message and send it to Sentry.
 */
export const captureMessage = (message: string, level: Sentry.SeverityLevel = "info") => {
  Sentry.captureMessage(message, level);
};
